import React, { useEffect, useRef } from 'react';
import { settingsService } from '../services/SettingsService';
import { FolderService } from '../services/FolderService';

interface Book {
  isbn: string;
  book_title: string;
  page_count: number;
  fast_track: boolean;
  images_created: boolean;
  text_extracted_basic: boolean;
  text_extracted_blocked: boolean;
  text_extracted_enhanced: boolean;
  text_extracted?: string;
  text_extracted_combined?: boolean;
  text_extracted_files_prepared?: boolean;
  files_prepared_pages_processed?: number;
  possible_issues?: string;
  'ai-confidence'?: number;
  extracted_from?: string;
  approval?: {
    images?: boolean | string;
    words?: boolean | string;
    voice?: boolean | string;
    notes?: string;
  };
}

interface BookPagesTableProps {
  books: Book[];
  onOpenBook: (isbn: string) => void;
  /** When switching from info table: index in the shared filtered list of the first row to show (so first book matches). */
  initialFirstRowIndex?: number;
}

interface PageData {
  'page-index': number;
  filename: string;
  width: number;
  height: number;
  original?: string;
}

interface SpineData {
  pages: PageData[];
  copyright?: PageData[];
  archived?: PageData[];
  approval?: {
    images?: boolean | string;
    words?: boolean | string;
    voice?: boolean | string;
    notes?: string;
  };
  book?: {
    fast_track?: boolean;
    voice_service?: string;
    voice_name?: string;
    tts_chars?: number;
    tts_cost_azure?: number;
    tts_cost_elevenlabs?: number;
    [key: string]: any;
  };
}

// Vanilla JS Pages Table Class
class BookPagesTableVanilla {
  private container: HTMLElement;
  private books: Book[] = [];
  private onOpenBook: (isbn: string) => void;
  private bookSpineData: Record<string, SpineData> = {};
  private originalSpineData: Record<string, SpineData> = {}; // Store original for comparison
  private loadingBooks: Set<string> = new Set();
  private failedSpineLoads: Set<string> = new Set(); // Track ISBNs that failed to load (404, etc.)
  private showingCopyright: Set<string> = new Set();
  private booksWithChanges: Set<string> = new Set(); // Track which books have unsaved changes
  private enlargeOnHover: boolean = true;
  private pagination = {
    pageIndex: 0,
    pageSize: 10
  };
  /** When set (e.g. after switching from info table), slice starts here so first row matches info table. */
  private sliceStartIndex: number = 0;
  private keyboardHandler: ((event: KeyboardEvent) => void) | null = null;
  private imageCache: Map<string, string> = new Map(); // Cache object URLs by key: `${isbn}:${filename}`

  constructor(
    container: HTMLElement,
    books: Book[],
    onOpenBook: (isbn: string) => void,
    initialFirstRowIndex?: number
  ) {
    this.container = container;
    this.books = books;
    this.onOpenBook = onOpenBook;

    // Restore saved state if available
    const savedState = settingsService.getPagesTableState();
    if (savedState) {
      console.log('[BookPagesTable] Restoring saved table state:', savedState);
      this.pagination.pageIndex = savedState.pagination.pageIndex;
      this.pagination.pageSize = Math.min(savedState.pagination.pageSize, 100);
    } else {
      // Fall back to individual settings
      this.pagination.pageIndex = settingsService.getImagesTablePageIndex();
      this.pagination.pageSize = Math.min(settingsService.getImagesTablePageSize(), 100);
    }

    this.sliceStartIndex = this.pagination.pageIndex * this.pagination.pageSize;

    // When switching from info table: start slice at this index so first row is the same (e.g. 26–35 not 21–30)
    if (initialFirstRowIndex !== undefined) {
      this.setSliceStartForFirstRowIndex(initialFirstRowIndex);
    }

    this.enlargeOnHover = settingsService.getPagesTableEnlargeOnHover();
    this.init();
  }

  /** Set slice start so the row at firstRowIndex (in full list) is the first row shown. Pages table only shows books with images. */
  private setSliceStartForFirstRowIndex(firstRowIndex: number): void {
    const booksWithImages = this.books.filter((b) => b.images_created);
    if (booksWithImages.length === 0) {
      this.sliceStartIndex = 0;
      this.pagination.pageIndex = 0;
      return;
    }
    // Index in the "books with images" subset for the book at firstRowIndex in the full list
    const countWithImagesUpTo = this.books
      .slice(0, firstRowIndex + 1)
      .filter((b) => b.images_created).length;
    const indexInSubset = Math.max(0, countWithImagesUpTo - 1);
    this.sliceStartIndex = Math.min(indexInSubset, Math.max(0, booksWithImages.length - this.pagination.pageSize));
    this.pagination.pageIndex = Math.floor(this.sliceStartIndex / this.pagination.pageSize);
  }

  private saveState(): void {
    const currentState = {
      filters: {}, // Pages table doesn't have filters
      pagination: {
        pageIndex: this.pagination.pageIndex,
        pageSize: this.pagination.pageSize,
      },
      searchQuery: '', // Pages table doesn't have search
    };
    settingsService.setPagesTableState(currentState);
    this.syncInfoTablePagination();
  }

  /** Sync only page index to info table so switching views keeps position; each table keeps its own page size. */
  private syncInfoTablePagination(): void {
    settingsService.setInfoTablePageIndex(this.pagination.pageIndex);
    const state = settingsService.getInfoTableState();
    if (state) {
      settingsService.setInfoTableState({
        ...state,
        pagination: { pageIndex: this.pagination.pageIndex, pageSize: state.pagination.pageSize },
      });
    }
  }

  private init(): void {
    this.render();
    this.setupKeyboardNavigation();
    this.setupEnlargeToggleListener();
  }

  private setupEnlargeToggleListener(): void {
    const handleToggle = (event: CustomEvent) => {
      const enabled = event.detail.enabled;
      this.enlargeOnHover = enabled;
      // Update all existing page pairs with the new setting
      const pagePairs = this.container.querySelectorAll('.page-pair');
      pagePairs.forEach((pair: Element) => {
        if (enabled) {
          pair.classList.add('enlarge-on-hover');
        } else {
          pair.classList.remove('enlarge-on-hover');
        }
      });
    };

    window.addEventListener('pagesTableEnlargeToggle', handleToggle as EventListener);
  }

  private setupKeyboardNavigation(): void {
    this.keyboardHandler = (event: KeyboardEvent) => {
      const booksWithImages = this.books.filter(book => book.images_created);
      const maxStart = Math.max(0, booksWithImages.length - this.pagination.pageSize);

      if (event.key === 'ArrowRight' && this.sliceStartIndex < maxStart) {
        event.preventDefault();
        this.sliceStartIndex = Math.min(maxStart, this.sliceStartIndex + this.pagination.pageSize);
        this.pagination.pageIndex = Math.floor(this.sliceStartIndex / this.pagination.pageSize);
        settingsService.setImagesTablePageIndex(this.pagination.pageIndex);
        this.saveState();
        this.render();
      } else if (event.key === 'ArrowLeft' && this.sliceStartIndex > 0) {
        event.preventDefault();
        this.sliceStartIndex = Math.max(0, this.sliceStartIndex - this.pagination.pageSize);
        this.pagination.pageIndex = Math.floor(this.sliceStartIndex / this.pagination.pageSize);
        settingsService.setImagesTablePageIndex(this.pagination.pageIndex);
        this.saveState();
        this.render();
      }
    };

    window.addEventListener('keydown', this.keyboardHandler);
  }

  private async loadSpineData(isbn: string, forceReload: boolean = false): Promise<void> {
    if ((this.bookSpineData[isbn] || this.loadingBooks.has(isbn)) && !forceReload) {
      return;
    }

    // Don't retry if we've already failed to load this spine
    if (this.failedSpineLoads.has(isbn) && !forceReload) {
      return;
    }

    this.loadingBooks.add(isbn);
    if (forceReload) {
      delete this.bookSpineData[isbn];
      this.failedSpineLoads.delete(isbn); // Clear failed status on force reload
    }

    try {
      const folder = FolderService.getFolder();
      if (!folder) {
        console.warn(`[BookPagesTable] No folder selected, cannot load spine for ${isbn}`);
        this.failedSpineLoads.add(isbn);
        return;
      }
      const { authenticatedFetch } = await import('../utils/api');
      const url = FolderService.addFolderToUrl(`/api/books/${isbn}/spine`);
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const spineData = await response.json();
        this.bookSpineData[isbn] = spineData;
        // Store original for comparison
        this.originalSpineData[isbn] = JSON.parse(JSON.stringify(spineData));
        // Clear failed status on successful load
        this.failedSpineLoads.delete(isbn);
      } else {
        // Mark as failed if we get a 404 or other error
        if (response.status === 404) {
          console.warn(`[BookPagesTable] Spine not found for ${isbn} (404), will not retry`);
        } else {
          console.warn(`[BookPagesTable] Failed to load spine for ${isbn}: ${response.status} ${response.statusText}`);
        }
        this.failedSpineLoads.add(isbn);
      }
    } catch (error) {
      console.error(`[BookPagesTable] Error loading spine for ${isbn}:`, error);
      this.failedSpineLoads.add(isbn);
    } finally {
      this.loadingBooks.delete(isbn);
      this.render();
    }
  }

  private getImageUrl(isbn: string, filename: string): string {
    const baseUrl = `/api/books/${isbn}/images/${filename}`;
    try {
      const folder = FolderService.getFolder();
      if (folder) {
        return FolderService.addFolderToUrl(baseUrl);
      }
    } catch (error) {
      console.error('[BookPagesTable] Failed to get folder for image URL:', error);
    }
    return baseUrl;
  }

  private async loadAuthenticatedImage(isbn: string, filename: string, imgElement: HTMLImageElement, loadingIndicator?: HTMLElement): Promise<void> {
    const cacheKey = `${isbn}:${filename}`;
    
    // Check if we already have this image cached
    const cachedObjectUrl = this.imageCache.get(cacheKey);
    if (cachedObjectUrl) {
      // Reuse cached object URL
      imgElement.src = cachedObjectUrl;
      if (loadingIndicator) {
        loadingIndicator.style.display = 'none';
      }
      return;
    }
    
    try {
      const { loadAuthenticatedImage } = await import('../utils/imageLoader');
      const imageUrl = this.getImageUrl(isbn, filename);
      const objectUrl = await loadAuthenticatedImage(imageUrl);
      
      // Cache the object URL
      this.imageCache.set(cacheKey, objectUrl);
      
      imgElement.src = objectUrl;
      
      // Hide loading indicator when image loads
      imgElement.addEventListener('load', () => {
        if (loadingIndicator) {
          loadingIndicator.style.display = 'none';
        }
      }, { once: true });
      
      // Clean up object URL when image errors (but keep in cache for retry)
      imgElement.addEventListener('error', () => {
        // Don't revoke the object URL - keep it in cache in case it was a transient error
        if (loadingIndicator) {
          loadingIndicator.style.display = 'none';
        }
      }, { once: true });
      
      // Note: We keep object URLs in cache and they're cleaned up when the page is unloaded
    } catch (error) {
      console.error(`[BookPagesTable] Failed to load authenticated image ${filename}:`, error);
      if (loadingIndicator) {
        loadingIndicator.style.display = 'none';
      }
      // Fallback to regular URL (will likely fail with 401, but at least we tried)
      imgElement.src = this.getImageUrl(isbn, filename);
    }
  }

  private createTrashIcon(): HTMLElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.style.cssText = 'color: var(--text-primary); cursor: pointer;';
    
    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M3 6h18');
    svg.appendChild(path1);
    
    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6');
    svg.appendChild(path2);
    
    const path3 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path3.setAttribute('d', 'M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2');
    svg.appendChild(path3);
    
    return svg as unknown as HTMLElement;
  }

  private createCopyrightIcon(): HTMLElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.style.cssText = 'color: var(--text-primary); cursor: pointer;';
    
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '10');
    svg.appendChild(circle);
    
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M14.5 9a3 3 0 1 0 0 6');
    svg.appendChild(path);
    
    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M9.5 9a3 3 0 1 0 0 6');
    svg.appendChild(path2);
    
    return svg as unknown as HTMLElement;
  }

  private createPlusIcon(): HTMLElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.style.cssText = 'color: var(--text-primary); cursor: pointer;';
    
    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M5 12h14');
    svg.appendChild(path1);
    
    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M12 5v14');
    svg.appendChild(path2);
    
    return svg as unknown as HTMLElement;
  }

  private async togglePageCopyright(isbn: string, pageIndex: number, isCurrentlyCopyright: boolean): Promise<void> {
    try {
      const spineData = this.bookSpineData[isbn];
      if (!spineData) {
        throw new Error('Spine data not loaded');
      }

      const sourcePages = isCurrentlyCopyright ? (spineData.copyright || []) : spineData.pages;
      if (!sourcePages || pageIndex >= sourcePages.length) {
        throw new Error('Page not found');
      }

      // Remove page from source section
      const page = sourcePages.splice(pageIndex, 1)[0];

      // Get or create target section
      const targetPages = isCurrentlyCopyright ? spineData.pages : (spineData.copyright || []);
      if (!spineData.copyright && !isCurrentlyCopyright) {
        spineData.copyright = [];
      }

      // Append to end of target section
      const targetSection = isCurrentlyCopyright ? spineData.pages : spineData.copyright!;
      targetSection.push(page);

      // Update page indices for both sections (starting at 1)
      for (let i = 0; i < sourcePages.length; i++) {
        sourcePages[i]['page-index'] = i + 1;
      }
      for (let i = 0; i < targetSection.length; i++) {
        targetSection[i]['page-index'] = i + 1;
      }

      // Update spine data
      if (isCurrentlyCopyright) {
        spineData.copyright = sourcePages.length > 0 ? sourcePages : undefined;
        spineData.pages = targetSection;
      } else {
        spineData.pages = sourcePages;
        spineData.copyright = targetSection;
      }

      // Mark book as having changes
      this.booksWithChanges.add(isbn);
      
      // Update local spine data
      this.bookSpineData[isbn] = spineData;

      // Redraw to show changes and Save button
      this.render();
    } catch (error) {
      console.error('[BookPagesTable] Error toggling page copyright:', error);
      alert(`Failed to move page: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async deletePage(isbn: string, pageIndex: number, isCopyright: boolean): Promise<void> {
    try {
      const spineData = this.bookSpineData[isbn];
      if (!spineData) {
        throw new Error('Spine data not loaded');
      }

      const pages = isCopyright ? (spineData.copyright || []) : spineData.pages;
      if (!pages || pageIndex >= pages.length) {
        throw new Error('Page not found');
      }

      const page = pages[pageIndex];
      
      // Check if it's an added blank page
      if (page.original === 'added-blank-page') {
        // Completely remove it
        pages.splice(pageIndex, 1);
      } else {
        // Move to archived
        if (!spineData.archived) {
          spineData.archived = [];
        }
        // Remove from current location
        const removedPage = pages.splice(pageIndex, 1)[0];
        // Add to archived
        spineData.archived.push(removedPage);
      }

      // Update page indices for remaining pages (starting at 1)
      for (let i = 0; i < pages.length; i++) {
        pages[i]['page-index'] = i + 1;
      }

      // Update spine data
      if (isCopyright) {
        spineData.copyright = pages;
      } else {
        spineData.pages = pages;
      }

      // Mark book as having changes
      this.booksWithChanges.add(isbn);
      
      // Update local spine data
      this.bookSpineData[isbn] = spineData;

      // Redraw to show changes and Save button
      this.render();
    } catch (error) {
      console.error('[BookPagesTable] Error deleting page:', error);
      alert(`Failed to delete page: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async saveChanges(isbn: string): Promise<void> {
    try {
      const spineData = this.bookSpineData[isbn];
      if (!spineData) {
        throw new Error('Spine data not loaded');
      }

      const { FolderService } = await import('../services/FolderService');
      const folder = FolderService.getFolder();
      if (!folder) {
        throw new Error('Folder is required but not set');
      }

      const { authenticatedPut } = await import('../utils/api');
      const url = FolderService.addFolderToUrl(`/api/books/${isbn}/spine`);
      const response = await authenticatedPut(url, { data: spineData });

      if (!response.ok) {
        throw new Error(`Failed to save spine: ${response.statusText}`);
      }

      // Update original spine data
      this.originalSpineData[isbn] = JSON.parse(JSON.stringify(spineData));
      
      // Remove from changes set
      this.booksWithChanges.delete(isbn);

      // Reload spine data from server to get fresh data
      await this.loadSpineData(isbn, true);
    } catch (error) {
      console.error('[BookPagesTable] Error saving changes:', error);
      alert(`Failed to save changes: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async copyBlankPageImage(isbn: string): Promise<void> {
    try {
      const { FolderService } = await import('../services/FolderService');
      const folder = FolderService.getFolder();
      if (!folder) {
        throw new Error('Folder is required but not set');
      }

      // Create a 1x1 white PNG as base64 (RGB white: 255,255,255)
      const blankPageBase64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAADElEQVR4nGP4//8/AAX+Av4N70a4AAAAAElFTkSuQmCC';
      const blankPageBuffer = Uint8Array.from(atob(blankPageBase64), c => c.charCodeAt(0));

      // Upload to the ISBN folder using PUT endpoint
      const { authenticatedFetch } = await import('../utils/api');
      const url = FolderService.addFolderToUrl(`/api/books/${isbn}/images/blank-page-1x1.png`);
      const response = await authenticatedFetch(url, {
        method: 'PUT',
        body: blankPageBuffer,
        headers: {
          'Content-Type': 'image/png'
        }
      });

      if (!response.ok) {
        // If PUT doesn't work, try POST
        const postUrl = FolderService.addFolderToUrl(`/api/books/${isbn}/images`);
        const formData = new FormData();
        const blob = new Blob([blankPageBuffer], { type: 'image/png' });
        formData.append('file', blob, 'blank-page-1x1.png');
        
        const postResponse = await authenticatedFetch(postUrl, {
          method: 'POST',
          body: formData
        });

        if (!postResponse.ok) {
          throw new Error(`Failed to copy blank page image: ${postResponse.statusText}`);
        }
      }
    } catch (error) {
      console.error('[BookPagesTable] Error copying blank page image:', error);
      throw error;
    }
  }

  private async addPageBefore(isbn: string, targetPageIndex: number, isCopyright: boolean): Promise<void> {
    try {
      const spineData = this.bookSpineData[isbn];
      if (!spineData) {
        throw new Error('Spine data not loaded');
      }

      const pages = isCopyright ? (spineData.copyright || []) : spineData.pages;
      if (!pages) {
        throw new Error('Pages array not found');
      }

      // Copy the blank page image (only if it doesn't exist)
      try {
        await this.copyBlankPageImage(isbn);
      } catch (error) {
        // If file already exists, that's okay - continue
        console.log('[BookPagesTable] Blank page image may already exist, continuing...');
      }

      // Create new page entry
      const newPage: PageData = {
        'page-index': targetPageIndex,
        filename: 'blank-page-1x1.png',
        width: 1,
        height: 1,
        original: 'added-blank-page'
      };

      // Insert the new page before the target page
      pages.splice(targetPageIndex, 0, newPage);

      // Update page indices for all pages (they may have shifted) (starting at 1)
      for (let i = 0; i < pages.length; i++) {
        pages[i]['page-index'] = i + 1;
      }

      // Update spine data
      if (isCopyright) {
        spineData.copyright = pages;
      } else {
        spineData.pages = pages;
      }

      // Mark book as having changes
      this.booksWithChanges.add(isbn);
      
      // Update local spine data
      this.bookSpineData[isbn] = spineData;

      // Redraw to show changes and Save button
      this.render();
    } catch (error) {
      console.error('[BookPagesTable] Error adding page:', error);
      alert(`Failed to add page: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private createPagePair(isbn: string, leftPageData: PageData | null, rightPageData: PageData | null, leftPageIndex: number | null, rightPageIndex: number | null, isCopyright: boolean = false): HTMLElement {
    const pagePairDiv = document.createElement('div');
    pagePairDiv.className = 'page-pair';
    if (this.enlargeOnHover) {
      pagePairDiv.classList.add('enlarge-on-hover');
    }

    // Left page
    if (leftPageData) {
      const leftContainer = document.createElement('div');
      leftContainer.className = 'page-half';
      leftContainer.style.cssText = 'position: relative; width: 50%; height: 100%;';
      
      // Loading indicator
      const leftLoadingIndicator = document.createElement('div');
      leftLoadingIndicator.className = 'page-half-loading-indicator';
      leftLoadingIndicator.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: rgba(255, 255, 255, 0.9); z-index: 1;';
      const leftSpinner = document.createElement('div');
      leftSpinner.className = 'page-half-loading-spinner';
      leftSpinner.style.cssText = 'width: 30px; height: 30px; border: 3px solid rgba(0, 0, 0, 0.1); border-top-color: #007bff; border-radius: 50%; animation: spin 1s linear infinite;';
      leftLoadingIndicator.appendChild(leftSpinner);
      leftContainer.appendChild(leftLoadingIndicator);
      
      const leftImg = document.createElement('img');
      leftImg.className = 'page-half-img';
      leftImg.alt = `Page ${leftPageData['page-index']}`;
      leftImg.style.cssText = 'width: 100%; height: 100%; object-fit: contain; display: block;';
      
      // Load authenticated image asynchronously
      this.loadAuthenticatedImage(isbn, leftPageData.filename, leftImg, leftLoadingIndicator);
      
      leftImg.onerror = () => {
        leftImg.style.display = 'none';
        const placeholder = document.createElement('div');
        placeholder.className = 'page-half-placeholder';
        placeholder.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%;';
        placeholder.textContent = `P${leftPageData['page-index']}`;
        leftContainer.appendChild(placeholder);
      };
      
      leftContainer.appendChild(leftImg);
      pagePairDiv.appendChild(leftContainer);
    } else {
      // Check if this is the first spread of main book (not copyright) and show cover image
      const isFirstSpread = !isCopyright && leftPageIndex === null && rightPageIndex === 0;
      
      if (isFirstSpread) {
        // Show cover image
        const coverContainer = document.createElement('div');
        coverContainer.className = 'page-half-placeholder';
        coverContainer.style.cssText = 'position: relative; display: flex; align-items: flex-start; justify-content: flex-start; padding: 0;';
        
        const coverImg = document.createElement('img');
        coverImg.src = `https://static.getcurioso.com/web/static/cover-images/212x265/${isbn}.png`;
        coverImg.alt = 'Cover';
        coverImg.style.cssText = 'width: 65%; height: auto; object-fit: contain; position: absolute; top: 0; left: 0;';
        coverImg.onerror = () => {
          // If cover image fails to load, just show empty placeholder
          coverImg.style.display = 'none';
        };
        
        coverContainer.appendChild(coverImg);
        pagePairDiv.appendChild(coverContainer);
      } else {
        // Regular empty placeholder
        const placeholder = document.createElement('div');
        placeholder.className = 'page-half-placeholder';
        pagePairDiv.appendChild(placeholder);
      }
    }

    // Right page
    if (rightPageData) {
      const rightContainer = document.createElement('div');
      rightContainer.className = 'page-half';
      rightContainer.style.cssText = 'position: relative; width: 50%; height: 100%;';
      
      // Loading indicator
      const rightLoadingIndicator = document.createElement('div');
      rightLoadingIndicator.className = 'page-half-loading-indicator';
      rightLoadingIndicator.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: rgba(255, 255, 255, 0.9); z-index: 1;';
      const rightSpinner = document.createElement('div');
      rightSpinner.className = 'page-half-loading-spinner';
      rightSpinner.style.cssText = 'width: 30px; height: 30px; border: 3px solid rgba(0, 0, 0, 0.1); border-top-color: #007bff; border-radius: 50%; animation: spin 1s linear infinite;';
      rightLoadingIndicator.appendChild(rightSpinner);
      rightContainer.appendChild(rightLoadingIndicator);
      
      const rightImg = document.createElement('img');
      rightImg.className = 'page-half-img';
      rightImg.alt = `Page ${rightPageData['page-index']}`;
      rightImg.style.cssText = 'width: 100%; height: 100%; object-fit: contain; display: block;';
      
      // Load authenticated image asynchronously
      this.loadAuthenticatedImage(isbn, rightPageData.filename, rightImg, rightLoadingIndicator);
      
      rightImg.onerror = () => {
        rightImg.style.display = 'none';
        const placeholder = document.createElement('div');
        placeholder.className = 'page-half-placeholder';
        placeholder.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%;';
        placeholder.textContent = `P${rightPageData['page-index']}`;
        rightContainer.appendChild(placeholder);
      };
      
      rightContainer.appendChild(rightImg);
      pagePairDiv.appendChild(rightContainer);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'page-half-placeholder';
      pagePairDiv.appendChild(placeholder);
    }

    // Create overlay for icons (only visible on hover)
    const overlay = document.createElement('div');
    overlay.className = 'page-pair-overlay';
    
    // Trash top left (for left page)
    const trashLeft = document.createElement('div');
    trashLeft.style.cssText = 'position: absolute; top: 8px; left: 8px; pointer-events: auto;';
    const trashLeftIcon = this.createTrashIcon();
    if (leftPageIndex !== null) {
      trashLeftIcon.onclick = (e) => {
        e.stopPropagation();
        this.deletePage(isbn, leftPageIndex, isCopyright);
      };
    }
    trashLeft.appendChild(trashLeftIcon);
    overlay.appendChild(trashLeft);
    
    // Copyright on left page (center of left half = 25% of total width)
    const copyrightLeft = document.createElement('div');
    copyrightLeft.style.cssText = 'position: absolute; top: 8px; left: 25%; transform: translateX(-50%); pointer-events: auto;';
    const copyrightLeftIcon = this.createCopyrightIcon();
    if (leftPageIndex !== null) {
      copyrightLeftIcon.onclick = (e) => {
        e.stopPropagation();
        this.togglePageCopyright(isbn, leftPageIndex, isCopyright);
      };
    }
    copyrightLeft.appendChild(copyrightLeftIcon);
    overlay.appendChild(copyrightLeft);
    
    // Trash center (for spread - deletes both pages, right first to avoid index shifting)
    const trashCenter = document.createElement('div');
    trashCenter.style.cssText = 'position: absolute; top: 8px; left: 50%; transform: translateX(-50%); pointer-events: auto;';
    const trashCenterIcon = this.createTrashIcon();
    trashCenterIcon.onclick = async (e) => {
      e.stopPropagation();
      // Delete both pages if they exist (delete right first to avoid index shifting)
      if (rightPageIndex !== null) {
        await this.deletePage(isbn, rightPageIndex, isCopyright);
      }
      if (leftPageIndex !== null) {
        await this.deletePage(isbn, leftPageIndex, isCopyright);
      }
    };
    trashCenter.appendChild(trashCenterIcon);
    overlay.appendChild(trashCenter);
    
    // Copyright on right page (center of right half = 75% of total width)
    const copyrightRight = document.createElement('div');
    copyrightRight.style.cssText = 'position: absolute; top: 8px; left: 75%; transform: translateX(-50%); pointer-events: auto;';
    const copyrightRightIcon = this.createCopyrightIcon();
    if (rightPageIndex !== null) {
      copyrightRightIcon.onclick = (e) => {
        e.stopPropagation();
        this.togglePageCopyright(isbn, rightPageIndex, isCopyright);
      };
    }
    copyrightRight.appendChild(copyrightRightIcon);
    overlay.appendChild(copyrightRight);
    
    // Trash top right (for right page)
    const trashRight = document.createElement('div');
    trashRight.style.cssText = 'position: absolute; top: 8px; right: 8px; pointer-events: auto;';
    const trashRightIcon = this.createTrashIcon();
    if (rightPageIndex !== null) {
      trashRightIcon.onclick = (e) => {
        e.stopPropagation();
        this.deletePage(isbn, rightPageIndex, isCopyright);
      };
    }
    trashRight.appendChild(trashRightIcon);
    overlay.appendChild(trashRight);
    
    // Plus icon on left side of left page (halfway up = 50% from top)
    const plusLeft = document.createElement('div');
    plusLeft.style.cssText = 'position: absolute; top: 50%; left: 8px; transform: translateY(-50%); pointer-events: auto;';
    const plusLeftIcon = this.createPlusIcon();
    if (leftPageIndex !== null) {
      plusLeftIcon.onclick = (e) => {
        e.stopPropagation();
        this.addPageBefore(isbn, leftPageIndex, isCopyright);
      };
    }
    plusLeft.appendChild(plusLeftIcon);
    overlay.appendChild(plusLeft);
    
    // Plus icon on left side of right page (halfway up, at 50% + 8px from left edge)
    const plusRight = document.createElement('div');
    plusRight.style.cssText = 'position: absolute; top: 50%; left: calc(50% + 8px); transform: translateY(-50%); pointer-events: auto;';
    const plusRightIcon = this.createPlusIcon();
    if (rightPageIndex !== null) {
      plusRightIcon.onclick = (e) => {
        e.stopPropagation();
        this.addPageBefore(isbn, rightPageIndex, isCopyright);
      };
    }
    plusRight.appendChild(plusRightIcon);
    overlay.appendChild(plusRight);
    
    pagePairDiv.appendChild(overlay);

    return pagePairDiv;
  }

  private createBookRow(book: Book): HTMLTableRowElement {
    const tr = document.createElement('tr');
    const spineData = this.bookSpineData[book.isbn];
    const isLoading = this.loadingBooks.has(book.isbn);

    // ISBN cell
    const isbnTd = document.createElement('td');
    isbnTd.style.cssText = 'vertical-align: top;';
    
    const isbnLink = document.createElement('a');
    isbnLink.href = '#';
    isbnLink.className = 'isbn-link';
    isbnLink.textContent = book.isbn;
    isbnLink.onclick = (e) => {
      e.preventDefault();
      this.onOpenBook(book.isbn);
    };
    isbnTd.appendChild(isbnLink);

    // Add buttons container for approval images and fast_track
    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.cssText = 'margin-top: 8px; display: flex; flex-direction: column; gap: 4px;';
    
    // Images Approved button (cycles: unapproved -> approved -> fail -> unapproved)
    const imagesApprovedButton = document.createElement('button');
    // Get current approval.images from spine or book (true, false, 'auto', or undefined)
    const currentApproval = spineData?.approval || book.approval || {};
    const currentImagesApproval = currentApproval.images;
    const getImagesApprovalStatus = () => {
      if (currentImagesApproval === true || currentImagesApproval === 'true') return 'approved';
      if (currentImagesApproval === false || currentImagesApproval === 'false') return 'fail';
      if (currentImagesApproval === 'auto') return 'auto';
      return 'unapproved';
    };
    const imagesApprovalStatus = getImagesApprovalStatus();
    imagesApprovedButton.className = `approval-btn images ${imagesApprovalStatus}`;
    imagesApprovedButton.style.cssText = 'padding: 4px 8px; font-size: 11px; border-radius: 4px; cursor: pointer; border: 1px solid var(--border-primary); width: 100%; text-align: left;';
    
    const getNextImagesApprovalValue = () => {
      // Cycle: unapproved/auto -> approved (true) -> fail (false) -> unapproved
      if (currentImagesApproval === true || currentImagesApproval === 'true') return false;
      if (currentImagesApproval === false || currentImagesApproval === 'false') return undefined;
      return true;
    };
    
    const updateImagesApprovedButton = (newValue: boolean | string | undefined) => {
      const status = newValue === true || newValue === 'true' ? 'approved' : newValue === false || newValue === 'false' ? 'fail' : newValue === 'auto' ? 'auto' : 'unapproved';
      imagesApprovedButton.className = `approval-btn images ${status}`;
      const icon = newValue === true || newValue === 'true' ? '🖼️✅' : newValue === false || newValue === 'false' ? '🖼️❌' : newValue === 'auto' ? '🖼️✓' : '🖼️';
      const text = newValue === true || newValue === 'true' ? 'Approved' : newValue === false || newValue === 'false' ? 'Fail' : newValue === 'auto' ? 'Auto' : 'Unapproved';
      imagesApprovedButton.innerHTML = `${icon} ${text}`;
      imagesApprovedButton.title = `Images: ${text}`;
    };
    
    updateImagesApprovedButton(currentImagesApproval);
    
    imagesApprovedButton.onclick = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Read current value dynamically
      const currentApprovalValue = (spineData?.approval || book.approval || {}).images;
      const currentValue = (currentApprovalValue === true || currentApprovalValue === false) ? currentApprovalValue : undefined;
      const newValue = currentValue === true ? false : currentValue === false ? undefined : true;
      
      try {
        const { FolderService } = await import('../services/FolderService');
        const { authenticatedPut } = await import('../utils/api');
        const folder = FolderService.getFolder();
        if (!folder) {
          throw new Error('Folder is required but not set');
        }
        // Get current approval object and update images field
        const currentApprovalObj = spineData?.approval || book.approval || {};
        const updatedApproval = {
          ...currentApprovalObj,
          images: newValue === undefined ? undefined : newValue
        };
        
        const url = FolderService.addFolderToUrl(`/api/books/${book.isbn}/approval`);
        const response = await authenticatedPut(url, { approval: updatedApproval });
        
        if (!response.ok) {
          throw new Error(`Failed to update approval: ${response.statusText}`);
        }
        
        // Update local book data
        if (!book.approval) {
          book.approval = {};
        }
        book.approval.images = newValue === undefined ? undefined : newValue;
        
        // Update spine data if available
        if (spineData) {
          if (!spineData.approval) {
            spineData.approval = {};
          }
          spineData.approval.images = newValue === undefined ? undefined : newValue;
        }
        
        updateImagesApprovedButton(newValue === undefined ? undefined : newValue);
        
        // Update the books array in the parent component
        const bookIndex = this.books.findIndex(b => b.isbn === book.isbn);
        if (bookIndex !== -1) {
          if (!this.books[bookIndex].approval) {
            this.books[bookIndex].approval = {};
          }
          this.books[bookIndex].approval.images = newValue === undefined ? undefined : newValue;
        }
      } catch (error) {
        console.error('[BookPagesTable] Error updating approval:', error);
        alert(`Failed to update approval: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };
    
    buttonsContainer.appendChild(imagesApprovedButton);
    
    // Fast Track button
    const fastTrackButton = document.createElement('button');
    // Handle null/undefined/empty as false for display - check spine first, then book
    const fastTrackValue = (spineData?.book?.fast_track === true) || (book.fast_track === true);
    const fastTrackStatus = fastTrackValue === true ? 'approved' : 'unapproved';
    fastTrackButton.className = `approval-btn words ${fastTrackStatus}`;
    fastTrackButton.style.cssText = 'padding: 4px 8px; font-size: 11px; border-radius: 4px; cursor: pointer; border: 1px solid var(--border-primary); width: 100%; text-align: left;';
    
    const updateFastTrackButton = (newValue: boolean) => {
      const status = newValue === true ? 'approved' : 'unapproved';
      fastTrackButton.className = `approval-btn words ${status}`;
      const icon = newValue === true ? '⚡✅' : '⚡';
      const text = newValue === true ? 'Fast Track' : 'Not Fast Track';
      fastTrackButton.innerHTML = `${icon} ${text}`;
      fastTrackButton.title = `Fast Track: ${text}`;
    };
    
    updateFastTrackButton(fastTrackValue);
    
    fastTrackButton.onclick = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Read current value dynamically from book or spine data
      const currentFastTrackValue = (spineData?.book?.fast_track === true) || (book.fast_track === true);
      const newValue = !currentFastTrackValue;
      try {
        const { FolderService } = await import('../services/FolderService');
        const { authenticatedPut } = await import('../utils/api');
        const folder = FolderService.getFolder();
        if (!folder) {
          throw new Error('Folder is required but not set');
        }
        const url = FolderService.addFolderToUrl(`/api/books/${book.isbn}/fast-track`);
        const response = await authenticatedPut(url, { fast_track: newValue });
        
        if (!response.ok) {
          throw new Error(`Failed to update fast_track: ${response.statusText}`);
        }
        
        // Update local book data
        book.fast_track = newValue;
        if (spineData && spineData.book) {
          spineData.book.fast_track = newValue;
        }
        updateFastTrackButton(newValue);
        
        // Update the books array in the parent component
        const bookIndex = this.books.findIndex(b => b.isbn === book.isbn);
        if (bookIndex !== -1) {
          this.books[bookIndex].fast_track = newValue;
        }
      } catch (error) {
        console.error('[BookPagesTable] Error updating fast_track:', error);
        alert(`Failed to update fast_track: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };
    
    buttonsContainer.appendChild(fastTrackButton);
    isbnTd.appendChild(buttonsContainer);

    // Add copyright toggle links if copyright pages exist
    if (spineData && spineData.copyright && spineData.copyright.length > 0) {
      const hasCopyright = true;
      const isShowingCopyright = this.showingCopyright.has(book.isbn);
      
      const toggleDiv = document.createElement('div');
      toggleDiv.style.cssText = 'margin-top: 4px; display: flex; flex-direction: column; gap: 2px;';
      
      // Main book link
      const mainBookLink = document.createElement('div');
      mainBookLink.style.cssText = `font-size: 11px; color: ${!isShowingCopyright ? 'var(--text-primary)' : 'var(--text-secondary)'}; cursor: pointer; text-decoration: ${!isShowingCopyright ? 'underline' : 'none'};`;
      mainBookLink.textContent = 'main book';
      mainBookLink.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isShowingCopyright) {
          this.showingCopyright.delete(book.isbn);
          this.render();
        }
      };
      toggleDiv.appendChild(mainBookLink);
      
      // Copyright link
      const copyrightLink = document.createElement('div');
      copyrightLink.style.cssText = `font-size: 11px; color: ${isShowingCopyright ? 'var(--text-primary)' : 'var(--text-secondary)'}; cursor: pointer; text-decoration: ${isShowingCopyright ? 'underline' : 'none'};`;
      copyrightLink.textContent = 'copyright';
      copyrightLink.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isShowingCopyright) {
          this.showingCopyright.add(book.isbn);
          this.render();
        }
      };
      toggleDiv.appendChild(copyrightLink);
      
      isbnTd.appendChild(toggleDiv);
    }

    // Add Save button if there are unsaved changes
    if (this.booksWithChanges.has(book.isbn)) {
      const saveButtonDiv = document.createElement('div');
      saveButtonDiv.style.cssText = 'margin-top: 8px;';
      
      const saveButton = document.createElement('button');
      saveButton.textContent = 'Save';
      saveButton.style.cssText = 'padding: 4px 8px; background-color: var(--accent-primary); color: white; border: 1px solid var(--accent-primary); border-radius: 4px; cursor: pointer; font-size: 12px;';
      saveButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.saveChanges(book.isbn);
      };
      
      saveButtonDiv.appendChild(saveButton);
      isbnTd.appendChild(saveButtonDiv);
    }

    if (isLoading) {
      const loadingTd = document.createElement('td');
      loadingTd.colSpan = 6; // ISBN + 6 data columns (Start, Pages 2-3, Pages 4-5, ..., Last 2, Last 1)
      loadingTd.style.cssText = 'text-align: center; color: #6c757d;';
      loadingTd.textContent = 'Loading pages...';
      tr.appendChild(isbnTd);
      tr.appendChild(loadingTd);
      return tr;
    }

    if (!spineData || !spineData.pages) {
      const noDataTd = document.createElement('td');
      noDataTd.colSpan = 6; // ISBN + 6 data columns (Start, Pages 2-3, Pages 4-5, ..., Last 2, Last 1)
      noDataTd.style.cssText = 'text-align: center; color: #dc3545;';
      noDataTd.textContent = 'No spine data available';
      tr.appendChild(isbnTd);
      tr.appendChild(noDataTd);
      return tr;
    }

    tr.appendChild(isbnTd);

    const isShowingCopyright = this.showingCopyright.has(book.isbn);
    const copyrightPages = spineData.copyright || [];
    const copyrightTotalPages = copyrightPages.length;
    const hasCopyright = copyrightTotalPages > 0;

    // Show copyright pages or regular pages based on toggle
    if (isShowingCopyright && hasCopyright) {
      const copyrightIsOdd = copyrightTotalPages % 2 === 1;

      // Start spread: {empty page} & copyright page 1
      const startTd = document.createElement('td');
      startTd.style.cssText = 'text-align: center; padding: 0.5rem;';
      const startPair = this.createPagePair(
        book.isbn,
        null,
        copyrightPages[0] ? {...copyrightPages[0], 'page-index': copyrightPages[0]['page-index']} : null,
        null,
        0,
        true
      );
      startTd.appendChild(startPair);
      tr.appendChild(startTd);

      // Copyright Pages 2-3
      const pages23Td = document.createElement('td');
      pages23Td.style.cssText = 'text-align: center; padding: 0.5rem;';
      const pages23Pair = this.createPagePair(
        book.isbn,
        copyrightPages[1] ? {...copyrightPages[1], 'page-index': copyrightPages[1]['page-index']} : null,
        copyrightPages[2] ? {...copyrightPages[2], 'page-index': copyrightPages[2]['page-index']} : null,
        1,
        2,
        true
      );
      pages23Td.appendChild(pages23Pair);
      tr.appendChild(pages23Td);

      // Copyright Pages 4-5
      const pages45Td = document.createElement('td');
      pages45Td.style.cssText = 'text-align: center; padding: 0.5rem;';
      const pages45Pair = this.createPagePair(
        book.isbn,
        copyrightPages[3] ? {...copyrightPages[3], 'page-index': copyrightPages[3]['page-index']} : null,
        copyrightPages[4] ? {...copyrightPages[4], 'page-index': copyrightPages[4]['page-index']} : null,
        3,
        4,
        true
      );
      pages45Td.appendChild(pages45Pair);
      tr.appendChild(pages45Td);

      // Only show ellipsis and last 2 spreads if there are more than 6 pages (first 3 spreads = 6 pages)
      if (copyrightTotalPages > 6) {
        // Ellipsis
        const ellipsisTd = document.createElement('td');
        ellipsisTd.style.cssText = 'text-align: center; font-weight: bold;';
        ellipsisTd.textContent = '...';
        tr.appendChild(ellipsisTd);

        // Second-to-last spread (last 2 spreads)
        const secondLastTd = document.createElement('td');
        secondLastTd.style.cssText = 'text-align: center; padding: 0.5rem;';
        let secondLastPair: HTMLElement;
        if (copyrightIsOdd) {
          // Odd copyright pages: second-to-last spread is pages N-3 & N-2
          secondLastPair = this.createPagePair(
            book.isbn,
            copyrightTotalPages >= 4 ? {...copyrightPages[copyrightTotalPages - 4], 'page-index': copyrightPages[copyrightTotalPages - 4]['page-index']} : null,
            copyrightTotalPages >= 3 ? {...copyrightPages[copyrightTotalPages - 3], 'page-index': copyrightPages[copyrightTotalPages - 3]['page-index']} : null,
            copyrightTotalPages >= 4 ? copyrightTotalPages - 4 : null,
            copyrightTotalPages >= 3 ? copyrightTotalPages - 3 : null,
            true
          );
        } else {
          // Even copyright pages: last spread is N|empty, so second-to-last is N-2|N-1
          secondLastPair = this.createPagePair(
            book.isbn,
            copyrightTotalPages >= 3 ? {...copyrightPages[copyrightTotalPages - 3], 'page-index': copyrightPages[copyrightTotalPages - 3]['page-index']} : null,
            copyrightTotalPages >= 2 ? {...copyrightPages[copyrightTotalPages - 2], 'page-index': copyrightPages[copyrightTotalPages - 2]['page-index']} : null,
            copyrightTotalPages >= 3 ? copyrightTotalPages - 3 : null,
            copyrightTotalPages >= 2 ? copyrightTotalPages - 2 : null,
            true
          );
        }
        secondLastTd.appendChild(secondLastPair);
        tr.appendChild(secondLastTd);

        // Last spread
        const lastTd = document.createElement('td');
        lastTd.style.cssText = 'text-align: center; padding: 0.5rem;';
        let lastPair: HTMLElement;
        if (copyrightIsOdd) {
          // Odd copyright pages: last spread is pages N-1 & N
          lastPair = this.createPagePair(
            book.isbn,
            copyrightTotalPages >= 2 ? {...copyrightPages[copyrightTotalPages - 2], 'page-index': copyrightPages[copyrightTotalPages - 2]['page-index']} : null,
            copyrightTotalPages >= 1 ? {...copyrightPages[copyrightTotalPages - 1], 'page-index': copyrightPages[copyrightTotalPages - 1]['page-index']} : null,
            copyrightTotalPages >= 2 ? copyrightTotalPages - 2 : null,
            copyrightTotalPages >= 1 ? copyrightTotalPages - 1 : null,
            true
          );
        } else {
          // Even copyright pages: last spread is page N alone
          lastPair = this.createPagePair(
            book.isbn,
            copyrightTotalPages >= 1 ? {...copyrightPages[copyrightTotalPages - 1], 'page-index': copyrightPages[copyrightTotalPages - 1]['page-index']} : null,
            null,
            copyrightTotalPages >= 1 ? copyrightTotalPages - 1 : null,
            null,
            true
          );
        }
        lastTd.appendChild(lastPair);
        tr.appendChild(lastTd);
      }
    } else {
      // Show regular pages
      const pages = spineData.pages;
      const totalPages = pages.length;
      const isOdd = totalPages % 2 === 1;

      // Start spread: {empty page} & page 1
      const startTd = document.createElement('td');
      startTd.style.cssText = 'text-align: center; padding: 0.5rem;';
      const startPair = this.createPagePair(book.isbn, null, pages[0] ? {...pages[0], 'page-index': 1} : null, null, 0, false);
      startTd.appendChild(startPair);
      tr.appendChild(startTd);

      // Pages 2-3
      const pages23Td = document.createElement('td');
      pages23Td.style.cssText = 'text-align: center; padding: 0.5rem;';
      const pages23Pair = this.createPagePair(
        book.isbn,
        pages[1] ? {...pages[1], 'page-index': 2} : null,
        pages[2] ? {...pages[2], 'page-index': 3} : null,
        1,
        2,
        false
      );
      pages23Td.appendChild(pages23Pair);
      tr.appendChild(pages23Td);

      // Pages 4-5
      const pages45Td = document.createElement('td');
      pages45Td.style.cssText = 'text-align: center; padding: 0.5rem;';
      const pages45Pair = this.createPagePair(
        book.isbn,
        pages[3] ? {...pages[3], 'page-index': 4} : null,
        pages[4] ? {...pages[4], 'page-index': 5} : null,
        3,
        4,
        false
      );
      pages45Td.appendChild(pages45Pair);
      tr.appendChild(pages45Td);

      // Only show ellipsis and last 2 spreads if there are more than 6 pages (first 3 spreads = 6 pages)
      if (totalPages > 6) {
        // Ellipsis
        const ellipsisTd = document.createElement('td');
        ellipsisTd.style.cssText = 'text-align: center; font-weight: bold;';
        ellipsisTd.textContent = '...';
        tr.appendChild(ellipsisTd);

        // Second-to-last spread (last 2 spreads)
        const secondLastTd = document.createElement('td');
        secondLastTd.style.cssText = 'text-align: center; padding: 0.5rem;';
        let secondLastPair: HTMLElement;
        if (isOdd) {
          // Odd pages: last spread is N-1|N, so second-to-last is N-3|N-2
          secondLastPair = this.createPagePair(
            book.isbn,
            totalPages >= 4 ? {...pages[totalPages - 4], 'page-index': totalPages - 3} : null,
            totalPages >= 3 ? {...pages[totalPages - 3], 'page-index': totalPages - 2} : null,
            totalPages >= 4 ? totalPages - 4 : null,
            totalPages >= 3 ? totalPages - 3 : null,
            false
          );
        } else {
          // Even pages: last spread is N|empty, so second-to-last is N-2|N-1
          secondLastPair = this.createPagePair(
            book.isbn,
            totalPages >= 3 ? {...pages[totalPages - 3], 'page-index': totalPages - 2} : null,
            totalPages >= 2 ? {...pages[totalPages - 2], 'page-index': totalPages - 1} : null,
            totalPages >= 3 ? totalPages - 3 : null,
            totalPages >= 2 ? totalPages - 2 : null,
            false
          );
        }
        secondLastTd.appendChild(secondLastPair);
        tr.appendChild(secondLastTd);

        // Last spread
        const lastTd = document.createElement('td');
        lastTd.style.cssText = 'text-align: center; padding: 0.5rem;';
        let lastPair: HTMLElement;
        if (isOdd) {
          // Odd pages: last spread is pages N-1 & N
          lastPair = this.createPagePair(
            book.isbn,
            totalPages >= 2 ? {...pages[totalPages - 2], 'page-index': totalPages - 1} : null,
            totalPages >= 1 ? {...pages[totalPages - 1], 'page-index': totalPages} : null,
            totalPages >= 2 ? totalPages - 2 : null,
            totalPages >= 1 ? totalPages - 1 : null,
            false
          );
        } else {
          // Even pages: last spread is page N alone with empty right side
          lastPair = this.createPagePair(
            book.isbn,
            totalPages >= 1 ? {...pages[totalPages - 1], 'page-index': totalPages} : null,
            null,
            totalPages >= 1 ? totalPages - 1 : null,
            null,
            false
          );
        }
        lastTd.appendChild(lastPair);
        tr.appendChild(lastTd);
      }
    }

    return tr;
  }

  private render(): void {
    const booksWithImages = this.books.filter(book => book.images_created);
    const maxStart = Math.max(0, booksWithImages.length - this.pagination.pageSize);
    const start = Math.min(this.sliceStartIndex, maxStart);
    if (start !== this.sliceStartIndex) {
      this.sliceStartIndex = start;
      this.pagination.pageIndex = Math.floor(start / this.pagination.pageSize);
    }
    const end = start + this.pagination.pageSize;
    const paginatedBooks = booksWithImages.slice(start, end);

    // Load spine data for paginated books
    paginatedBooks.forEach(book => {
      if (book.images_created && !this.bookSpineData[book.isbn] && !this.loadingBooks.has(book.isbn) && !this.failedSpineLoads.has(book.isbn)) {
        this.loadSpineData(book.isbn);
      }
    });

    // Clear container
    this.container.innerHTML = '';

    // Create table wrapper
    const tableWrapper = document.createElement('div');
    tableWrapper.className = 'books-images-table';

    // Create table
    const table = document.createElement('table');
    
    // Create thead
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    const headers = ['ISBN', 'Start', 'Pages 2-3', 'Pages 4-5', '...', 'Last 2', 'Last 1'];
    headers.forEach(headerText => {
      const th = document.createElement('th');
      th.textContent = headerText;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Create tbody
    const tbody = document.createElement('tbody');
    paginatedBooks.forEach(book => {
      const row = this.createBookRow(book);
      tbody.appendChild(row);
    });
    table.appendChild(tbody);

    tableWrapper.appendChild(table);

    // Create pagination
    const paginationContainer = document.createElement('div');
    paginationContainer.className = 'pagination-container';

    const paginationInfo = document.createElement('div');
    paginationInfo.className = 'pagination-info';

    const paginationText = document.createElement('span');
    paginationText.className = 'pagination-text';
    paginationText.textContent = `Showing ${start + 1} to ${Math.min(end, booksWithImages.length)} of ${booksWithImages.length} results`;
    paginationInfo.appendChild(paginationText);

    const itemsPerPage = document.createElement('div');
    itemsPerPage.className = 'items-per-page';

    const label = document.createElement('label');
    label.htmlFor = 'items-per-page-pages';
    label.textContent = 'Items per page:';
    itemsPerPage.appendChild(label);

    const select = document.createElement('select');
    select.id = 'items-per-page-pages';
    select.value = this.pagination.pageSize.toString();
    [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 50, 100].forEach(size => {
      const option = document.createElement('option');
      option.value = size.toString();
      option.textContent = size.toString();
      if (size === this.pagination.pageSize) {
        option.selected = true;
      }
      select.appendChild(option);
    });

    select.onchange = (e) => {
      const newSize = Math.min(Number((e.target as HTMLSelectElement).value), 100);
      this.pagination.pageSize = newSize;
      this.sliceStartIndex = 0;
      this.pagination.pageIndex = 0;
      settingsService.setImagesTablePageSize(newSize);
      settingsService.setImagesTablePageIndex(0);
      this.saveState();
      this.render();
    };

    itemsPerPage.appendChild(select);
    paginationInfo.appendChild(itemsPerPage);
    paginationContainer.appendChild(paginationInfo);

    const paginationControls = document.createElement('div');
    paginationControls.className = 'pagination-controls';

    const totalPages = Math.ceil(booksWithImages.length / this.pagination.pageSize);
    const atFirst = this.sliceStartIndex <= 0;
    const atLast = this.sliceStartIndex >= maxStart;

    const firstBtn = document.createElement('button');
    firstBtn.className = 'pagination-btn';
    firstBtn.textContent = 'First';
    firstBtn.disabled = atFirst;
    firstBtn.onclick = () => {
      this.sliceStartIndex = 0;
      this.pagination.pageIndex = 0;
      settingsService.setImagesTablePageIndex(0);
      this.saveState();
      this.render();
    };
    paginationControls.appendChild(firstBtn);

    const prevBtn = document.createElement('button');
    prevBtn.className = 'pagination-btn';
    prevBtn.textContent = 'Previous';
    prevBtn.disabled = atFirst;
    prevBtn.onclick = () => {
      this.sliceStartIndex = Math.max(0, this.sliceStartIndex - this.pagination.pageSize);
      this.pagination.pageIndex = Math.floor(this.sliceStartIndex / this.pagination.pageSize);
      settingsService.setImagesTablePageIndex(this.pagination.pageIndex);
      this.saveState();
      this.render();
    };
    paginationControls.appendChild(prevBtn);

    const pageGroup = document.createElement('span');
    pageGroup.className = 'pagination-dots pagination-page-group';
    pageGroup.appendChild(document.createTextNode('Page '));
    const pageInput = document.createElement('input');
    pageInput.type = 'number';
    pageInput.className = 'pagination-page-input';
    pageInput.min = '1';
    pageInput.max = String(Math.max(1, totalPages));
    pageInput.value = String(this.pagination.pageIndex + 1);
    pageInput.setAttribute('aria-label', 'Current page');
    pageInput.onkeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const requested = parseInt(pageInput.value, 10);
        if (!isNaN(requested) && totalPages >= 1) {
          const page = Math.min(totalPages, Math.max(1, requested));
          this.sliceStartIndex = (page - 1) * this.pagination.pageSize;
          this.pagination.pageIndex = page - 1;
          settingsService.setImagesTablePageIndex(this.pagination.pageIndex);
          this.saveState();
          this.render();
        }
      }
    };
    pageInput.onblur = () => {
      pageInput.value = String(this.pagination.pageIndex + 1);
    };
    pageGroup.appendChild(pageInput);
    pageGroup.appendChild(document.createTextNode(` of ${totalPages}`));
    paginationControls.appendChild(pageGroup);

    const nextBtn = document.createElement('button');
    nextBtn.className = 'pagination-btn';
    nextBtn.textContent = 'Next';
    nextBtn.disabled = atLast;
    nextBtn.onclick = () => {
      this.sliceStartIndex = Math.min(maxStart, this.sliceStartIndex + this.pagination.pageSize);
      this.pagination.pageIndex = Math.floor(this.sliceStartIndex / this.pagination.pageSize);
      settingsService.setImagesTablePageIndex(this.pagination.pageIndex);
      this.saveState();
      this.render();
    };
    paginationControls.appendChild(nextBtn);

    const lastBtn = document.createElement('button');
    lastBtn.className = 'pagination-btn';
    lastBtn.textContent = 'Last';
    lastBtn.disabled = atLast;
    lastBtn.onclick = () => {
      this.sliceStartIndex = (totalPages - 1) * this.pagination.pageSize;
      this.pagination.pageIndex = totalPages - 1;
      settingsService.setImagesTablePageIndex(this.pagination.pageIndex);
      this.saveState();
      this.render();
    };
    paginationControls.appendChild(lastBtn);

    paginationContainer.appendChild(paginationControls);
    tableWrapper.appendChild(paginationContainer);

    this.container.appendChild(tableWrapper);
  }

  public updateBooks(books: Book[], firstRowIndex?: number): void {
    this.books = books;
    if (firstRowIndex !== undefined) {
      this.setSliceStartForFirstRowIndex(firstRowIndex);
      settingsService.setImagesTablePageIndex(this.pagination.pageIndex);
    } else {
      this.sliceStartIndex = 0;
      this.pagination.pageIndex = 0;
      settingsService.setImagesTablePageIndex(0);
    }
    this.saveState();
    this.render();
  }

  public destroy(): void {
    if (this.keyboardHandler) {
      window.removeEventListener('keydown', this.keyboardHandler);
      this.keyboardHandler = null;
    }
    this.container.innerHTML = '';
  }
}

// React wrapper component
const BookPagesTable: React.FC<BookPagesTableProps> = ({
  books,
  onOpenBook,
  initialFirstRowIndex,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableInstanceRef = useRef<BookPagesTableVanilla | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Create vanilla JS table instance (when switching to pages we get books + initialFirstRowIndex)
    tableInstanceRef.current = new BookPagesTableVanilla(
      containerRef.current,
      books,
      onOpenBook,
      initialFirstRowIndex
    );

    return () => {
      if (tableInstanceRef.current) {
        tableInstanceRef.current.destroy();
        tableInstanceRef.current = null;
      }
    };
  }, []); // Only run once on mount

  useEffect(() => {
    if (tableInstanceRef.current) {
      tableInstanceRef.current.updateBooks(books, initialFirstRowIndex);
    }
  }, [books, initialFirstRowIndex]);

  return <div ref={containerRef} />;
};

export default BookPagesTable;

